import {
  useGetExternalAccountsSuspense,
  useGetTransactions,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { FormatBillingAmount } from './formatBillingAmount';
import { SyncTransactionModal } from './SyncTransactionModal';

type TransactionRow = {
  id: string;
  external_id: string;
  amount: string;
  currency: string;
  posted: string;
  unallocated_amount: string;
};

const transactionSchema: ColumnDefinition<TransactionRow>[] = [
  {
    title: 'External ID',
    key: 'external_id',
    colspan: 'col-span-2',
  },
  {
    title: 'Posted',
    colspan: 'col-span-2',
    format: (row: TransactionRow) => (
      <span>
        {new Date(row.posted).toDateString().split(' ').slice(1).join(' ')}
      </span>
    ),
  },
  {
    title: 'Amount',
    justify: 'right',
    colspan: 'col-span-1',
    format: (row: TransactionRow) => {
      const code = row.currency as CurrencyCode;
      const precision = currencyPrecisionMap[code] || 2;
      return (
        <FormatBillingAmount
          amount={Math.abs(Number(row.amount))}
          currencyCode={code}
          currencyPrecision={precision}
        />
      );
    },
  },
  {
    title: 'Unallocated',
    justify: 'right',
    colspan: 'col-span-1',
    format: (row: TransactionRow) => {
      const code = row.currency as CurrencyCode;
      const precision = currencyPrecisionMap[code] || 2;
      return (
        <FormatBillingAmount
          amount={Math.abs(Number(row.unallocated_amount))}
          currencyCode={code}
          currencyPrecision={precision}
        />
      );
    },
  },
];

const ColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-2">External ID</div>
    <div className="col-span-2">Posted</div>
    <div className="col-span-1 text-right">Amount</div>
    <div className="col-span-1 text-right">Unallocated</div>
  </div>
);

const transactionEmptyValue = {
  'External ID': 'No Transactions',
  Posted: 'N/A',
  Amount: 'N/A',
  Unallocated: 'N/A',
};

/** Small triangle badge — same as nav */
const TriangleBadge = ({ size = 14 }: { size?: number }) => {
  const points = `${size / 2},1 ${size - 1},${size - 1} 1,${size - 1}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
    >
      <polygon
        points={points}
        fill="rgb(var(--colour-main-500))"
        stroke="rgb(var(--colour-main-500))"
        strokeWidth={1}
        strokeLinejoin="round"
      />
    </svg>
  );
};

type Props = {
  workspaceId: string;
  accountId: string;
};

const noop = (): void => {};

const AccountDetailLoading = (): JSX.Element => (
  <PaymentViewLayout title="" onBack={noop}>
    <div className="text-xmd font-helvetica flex flex-col w-full">
      {/* Title row */}
      <div className="flex w-full items-start border-b-[0.5px] border-main-200 h-f12">
        <div className="flex items-start gap-x-f1 p-[13.5px] flex-1 min-w-0 h-full">
          <div className="flex items-center gap-x-[8px] shrink-0">
            <TriangleBadge size={24} />
            <span className="text-lg text-transparent">Loading</span>
          </div>
        </div>
      </div>

      {/* External ID row */}
      <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
        <div
          className={clsx(
            'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
            kLabelColumnWidth
          )}
        >
          External ID
        </div>
        <div className="px-[13.5px] flex-1" />
      </div>

      {/* ID row */}
      <div className="flex items-center border-b-[0.5px] border-main-300 w-full h-f4">
        <div
          className={clsx(
            'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
            kLabelColumnWidth
          )}
        >
          ID
        </div>
        <div className="px-[13.5px] flex-1" />
      </div>

      {/* Tab row */}
      <div className="flex items-end w-full">
        <div className="px-[13.5px] py-f1 text-xmd text-main-500">
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </div>
        <div className="px-[13.5px] py-f1 text-xmd text-main-500 border-b-[0.5px] border-l-[0.5px] border-main-300">
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </div>
        <div className="flex-1 border-b-[0.5px] border-main-300" />
      </div>

      {/* Column headers */}
      <ColumnHeaders />
    </div>
  </PaymentViewLayout>
);

const AccountDetailPageContent = ({
  workspaceId,
  accountId,
}: Props): JSX.Element => {
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'unreconciled' | 'reconciled'>(
    'unreconciled'
  );
  const { data: accountsData } = useGetExternalAccountsSuspense();
  const { data: unreconciledData } = useGetTransactions({
    account: accountId,
    reconciliation_status: 'unreconciled',
  });
  const { data: reconciledData } = useGetTransactions({
    account: accountId,
    reconciliation_status: 'reconciled',
  });

  const handleBack = () => {
    window.history.back();
  };

  const handleCreate = useCallback(() => {
    setShowSyncModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowSyncModal(false);
  }, []);

  const account = useMemo(
    () => accountsData.data.find((acc) => acc.id === accountId),
    [accountsData.data, accountId]
  );

  useDocumentTitle(account?.name ?? 'Account');

  const transactions: TransactionRow[] = useMemo(() => {
    const source =
      selectedTab === 'reconciled' ? reconciledData : unreconciledData;
    if (!source?.data) return [];
    return source.data.map((transaction) => ({
      id: transaction.id,
      external_id: transaction.external_id,
      amount: transaction.amount,
      currency: transaction.currency,
      posted: transaction.posted,
      unallocated_amount: transaction.unallocated_amount,
    }));
  }, [selectedTab, unreconciledData, reconciledData]);

  const getPath = useCallback(
    (transaction: TransactionRow) =>
      makePath(['w', workspaceId, 'payments', 'transactions', transaction.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  return (
    <>
      <PaymentViewLayout
        title={account?.name ?? 'Account'}
        onBack={handleBack}
        primaryAction={{
          title: 'Sync',
          onClick: handleCreate,
          'data-testid': 'sync-transaction-button',
        }}
      >
        <div className="text-xmd font-helvetica flex flex-col w-full">
          {/* Title row */}
          <div className="flex w-full items-start border-b-[0.5px] border-main-200 h-f12">
            <div className="flex items-start gap-x-f1 p-[13.5px] flex-1 min-w-0 h-full">
              <div className="flex items-center gap-x-[8px] shrink-0">
                <TriangleBadge size={24} />
                <span className="text-lg text-main">{account?.name ?? ''}</span>
              </div>
            </div>
          </div>

          {/* External ID row */}
          <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
            <div
              className={clsx(
                'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
                kLabelColumnWidth
              )}
            >
              External ID
            </div>
            <div className="px-[13.5px] text-xmd text-main flex-1">
              {account?.external_id ?? ''}
            </div>
          </div>

          {/* ID row — double height */}
          <div className="flex items-start border-b-[0.5px] border-main-300 w-full h-f8">
            <div
              className={clsx(
                'px-[13.5px] pt-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
                kLabelColumnWidth
              )}
            >
              ID
            </div>
            <div className="px-[13.5px] pt-[13.5px] text-xmd text-main flex-1">
              {account?.id ?? ''}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-end w-full">
            <TabsV2
              options={[
                { label: 'Unreconciled', value: 'unreconciled' as const },
                { label: 'Reconciled', value: 'reconciled' as const },
              ]}
              selectedValue={selectedTab}
              onClick={setSelectedTab}
              borderColor="main-300"
            />
            <div className="flex-1 border-b-[0.5px] border-main-300" />
          </div>

          {/* Column headers */}
          <ColumnHeaders />

          {/* Transactions table */}
          <NavTableV2
            textSize="xmd"
            data={transactions}
            emptyValue={transactionEmptyValue}
            getPath={getPath}
            schema={transactionSchema}
            showHeader={false}
            stickyHeader={false}
            rowHeight="fixed"
          />
        </div>
      </PaymentViewLayout>
      {showSyncModal && (
        <SyncTransactionModal
          accountId={accountId}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export const AccountDetailPage = ({
  workspaceId,
  accountId,
}: Props): JSX.Element => (
  <Suspense fallback={<AccountDetailLoading />}>
    <AccountDetailPageContent workspaceId={workspaceId} accountId={accountId} />
  </Suspense>
);
